// Inline landing-page sign-up variants (Notion "SUF Changes: LP New Tests").
//
// A landing page collects some answers itself and hands them to sign-up
// through the hand-off route, then opens /sign-up?lp=<variant>. The variant
// reorders the insurance sign-up survey so eligibility comes first, adds a
// coverage preview (a quick check before member ID, height and weight) and
// leaves every other survey, and sign-up without `lp`, unchanged.

const QUESTION = {
  intro: 1589,
  state: 202503192,
  dateOfBirth: 11,
  firstName: 100,
  lastName: 131,
  height: 10,
  weight: 9,
  payer: 1586,
  memberId: 20250315,
  conditions: 5425,
  coverage: 101010,
} as const

// Not a Contentful id: the preview is a copy of the coverage question.
export const COVERAGE_PREVIEW_QUESTION_ID = 1010100

export const LP_VARIANTS = {
  // Landing page asks name, date of birth and carrier.
  v1: {
    signUpVariant: 'inline-lp-v1',
    handoff: [
      QUESTION.firstName,
      QUESTION.lastName,
      QUESTION.dateOfBirth,
      QUESTION.payer,
    ],
    order: [
      QUESTION.firstName,
      QUESTION.lastName,
      QUESTION.dateOfBirth,
      QUESTION.payer,
      QUESTION.state,
      COVERAGE_PREVIEW_QUESTION_ID,
      QUESTION.memberId,
      QUESTION.conditions,
      QUESTION.height,
      QUESTION.weight,
      QUESTION.coverage,
    ],
  },
  // Landing page asks carrier only, matching the insurance-list ad.
  v2: {
    signUpVariant: 'inline-lp-v2',
    handoff: [QUESTION.payer],
    order: [
      QUESTION.payer,
      QUESTION.firstName,
      QUESTION.lastName,
      QUESTION.state,
      QUESTION.dateOfBirth,
      COVERAGE_PREVIEW_QUESTION_ID,
      QUESTION.memberId,
      QUESTION.conditions,
      QUESTION.height,
      QUESTION.weight,
      QUESTION.coverage,
    ],
  },
} as const

export type LpVariantKey = keyof typeof LP_VARIANTS

/** The variant `?lp=` names, or null for anything else (sign-up then runs unchanged). */
export const lpVariantFrom = (value: unknown): LpVariantKey | null => {
  const key = Array.isArray(value) ? value[0] : value
  return typeof key === 'string' && Object.hasOwn(LP_VARIANTS, key)
    ? (key as LpVariantKey)
    : null
}

// The answers a hand-off writes to the user record rather than to survey
// responses, so sign-up can rebuild them on load.
export const MODEL_PATH_HANDOFF = {
  [QUESTION.firstName]: 'firstName',
  [QUESTION.lastName]: 'lastName',
  [QUESTION.dateOfBirth]: 'birthDate',
} as const

interface SurveyQuestionLike {
  id: number | string
  [key: string]: unknown
}

interface SurveyLike {
  sections: Array<{ name?: string; questions: SurveyQuestionLike[] }>
  [key: string]: unknown
}

/**
 * Reorder the insurance sign-up survey for a landing-page variant. The
 * variant's questions move to the front of the first section in the variant's
 * order, the intro screen is dropped (the landing page replaced it) and the
 * coverage preview is inserted. Returns the survey unchanged when any
 * question the variant needs is missing, so a Contentful edit falls back to
 * the normal flow instead of a broken one.
 */
export function applyLpVariant<S extends SurveyLike>(
  survey: S,
  variant: LpVariantKey
): S {
  const byId = new Map<number, SurveyQuestionLike>()
  survey.sections.forEach(section =>
    section.questions.forEach(question =>
      byId.set(Number(question.id), question)
    )
  )
  const coverage = byId.get(QUESTION.coverage)
  const order: readonly number[] = LP_VARIANTS[variant].order
  const needed = order.filter(id => id !== COVERAGE_PREVIEW_QUESTION_ID)
  if (!coverage || needed.some(id => !byId.has(id))) return survey

  const preview = {
    ...coverage,
    id: COVERAGE_PREVIEW_QUESTION_ID,
    softCheck: true,
  }
  const moved = new Set<number>([...order, QUESTION.intro])
  const front = order.map(id =>
    id === COVERAGE_PREVIEW_QUESTION_ID ? preview : byId.get(id)!
  )

  return {
    ...survey,
    sections: survey.sections.map((section, index) => {
      const rest = section.questions.filter(q => !moved.has(Number(q.id)))
      return { ...section, questions: index === 0 ? [...front, ...rest] : rest }
    }),
  }
}

/**
 * Survey responses for hand-off answers stored on the user record (name and
 * date of birth), for any the client has no response for yet. Sign-up reads
 * these like saved responses, so it starts after them and the eligibility
 * check has the values.
 */
export function handoffModelPathResponses(
  variant: LpVariantKey,
  user: {
    firstName?: string | null
    lastName?: string | null
    birthDate?: string | null
  } | null,
  existing: Array<{ surveyQuestionId: number | string }>
) {
  if (!user) return []
  const answered = new Set(existing.map(r => Number(r.surveyQuestionId)))
  const handoff: readonly number[] = LP_VARIANTS[variant].handoff
  return handoff
    .filter(id => id in MODEL_PATH_HANDOFF && !answered.has(id))
    .map(id => {
      const field = MODEL_PATH_HANDOFF[id as keyof typeof MODEL_PATH_HANDOFF]
      const value = user[field]
      return value
        ? {
            id: -id,
            surveyQuestionId: id,
            responseValue:
              field === 'birthDate'
                ? String(value).slice(0, 10)
                : String(value),
          }
        : null
    })
    .filter((response): response is NonNullable<typeof response> => !!response)
}
